'use client';

import { forwardRef } from 'react';
import ReactDatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { CalendarIcon } from 'lucide-react';

const MONTHS = [
	'January',
	'February',
	'March',
	'April',
	'May',
	'June',
	'July',
	'August',
	'September',
	'October',
	'November',
	'December'
];

export default function DatePicker({ value, onChange, disabled, hasError, placeholder }) {
	/**
	 * Formats a Date object to APA format string
	 * Returns: "2024, January 15"
	 */
	const formatToAPA = (date) => {
		if (!date) return '';
		const year = date.getFullYear();
		const month = MONTHS[date.getMonth()];
		const day = date.getDate();
		return `${year}, ${month} ${day}`;
	};

	/**
	 * Parses APA format date string to Date object
	 * Supports: "2024, January 15", "2024, January", "2024", "n.d."
	 */
	const parseAPADate = (dateStr) => {
		if (!dateStr || dateStr === 'n.d.') return null;

		// Try full date: "2024, January 15"
		const fullDateMatch = dateStr.match(/^(\d{4}),\s*(\w+)\s+(\d{1,2})$/);
		if (fullDateMatch) {
			const [, year, month, day] = fullDateMatch;
			const monthIndex = MONTHS.findIndex((m) => m.toLowerCase() === month.toLowerCase());
			if (monthIndex !== -1) {
				return new Date(parseInt(year), monthIndex, parseInt(day));
			}
		}

		// Try year and month: "2024, January"
		const yearMonthMatch = dateStr.match(/^(\d{4}),\s*(\w+)$/);
		if (yearMonthMatch) {
			const [, year, month] = yearMonthMatch;
			const monthIndex = MONTHS.findIndex((m) => m.toLowerCase() === month.toLowerCase());
			if (monthIndex !== -1) {
				return new Date(parseInt(year), monthIndex, 1);
			}
		}

		// Try year only: "2024"
		const yearMatch = dateStr.match(/^(\d{4})$/);
		if (yearMatch) {
			return new Date(parseInt(yearMatch[1]), 0, 1);
		}

		return null;
	};

	const selectedDate = parseAPADate(value);

	const handleDateChange = (date) => {
		if (date) {
			onChange(formatToAPA(date));
		}
	};

	// Custom input component
	const CustomInput = forwardRef(({ onClick }, ref) => (
		<div className="relative w-full">
			<input
				type="text"
				value={value || ''}
				onClick={onClick}
				ref={ref}
				placeholder={placeholder || '2024, January 15 or n.d.'}
				disabled={disabled}
				readOnly
				className={`w-full rounded-[10px] border px-4 xl:py-3 py-2 pr-10 text-sm focus:ring-1 text-dark-blue outline-none cursor-pointer ${
					disabled
						? 'bg-gray-100 cursor-not-allowed border-gray'
						: hasError
						? 'border-red-500 focus:ring-red-500'
						: 'border-gray focus:ring-purple'
				}`}
			/>
			<button
				type="button"
				onClick={onClick}
				disabled={disabled}
				className={`absolute right-3 top-1/2 -translate-y-1/2 transition-colors ${
					disabled ? 'text-gray-400 cursor-not-allowed' : 'text-purple hover:text-purple-dark'
				}`}
			>
				<CalendarIcon className="w-[18px] h-[18px]" />
			</button>
		</div>
	));

	CustomInput.displayName = 'CustomInput';

	return (
		<ReactDatePicker
			selected={selectedDate}
			onChange={handleDateChange}
			disabled={disabled}
			showYearDropdown
			showMonthDropdown
			dropdownMode="select"
			yearDropdownItemNumber={100}
			scrollableYearDropdown
			minDate={new Date(1900, 0, 1)}
			maxDate={new Date()}
			customInput={<CustomInput />}
			popperClassName="z-50"
			wrapperClassName="w-full block"
		/>
	);
}
